import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Alert, Button, Input, Modal, Table, Tag, Tooltip, Typography } from "antd";
import type { ColumnsType } from "antd/es/table";
import { DeleteOutlined, ExclamationCircleOutlined, ReloadOutlined, SearchOutlined } from "@ant-design/icons";
import { useMsal } from "@azure/msal-react";
import { getLoggedInUser } from "utils/utils";
import { useDebounce } from "hooks/useDebounce";
import {
    deleteCombinedGroup,
    listGroups,
    IGroupListItem,
} from "./accessGroupApi";
import { statusColor, statusLabel } from "./groupStatus";
import ResultPopup, { ResultStatus } from "./ResultPopup";

const { Title, Text } = Typography;

/** Group creation must reach this status before deletion is allowed. */
const COMPLETED_STATUS = "completed";

/** Drives the animated deletion popup. */
interface IDeleteProgress {
    status: ResultStatus;
    message: React.ReactNode;
}

interface IGroupListProps {
    /** Bumping this value triggers a re-fetch (e.g. after a successful create). */
    refreshKey: number;
    /** Called whenever the fetched group count changes (used for tab badge). */
    onCountChange?: (count: number) => void;
}

/** Formats an ISO date string into a readable date; returns "—" when absent. */
const formatDate = (value?: string): string => {
    if (!value) return "—";
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? value : date.toLocaleString();
};

/** Sorts items by created date descending (newest first). */
const byNewest = (a: IGroupListItem, b: IGroupListItem): number =>
    new Date(b.created_at ?? 0).getTime() - new Date(a.created_at ?? 0).getTime();

/** Renders a status value as a coloured tag. */
const statusTag = (status?: string) => {
    if (!status) return <Text type="secondary">—</Text>;
    return <Tag color={statusColor(status)}>{statusLabel(status)}</Tag>;
};

/** Renders a name with an inline copy button; shows "—" when empty. */
const copyableName = (value?: string) =>
    value ? (
        <Text copyable={{ text: value }}>{value}</Text>
    ) : (
        <Text type="secondary">—</Text>
    );

/** Strips the `ef.a.` prefix for display while keeping the full value copyable. */
const copyableEntitlementName = (value?: string) => {
    if (!value) return <Text type="secondary">—</Text>;
    const display = value.replace(/^ef\.a\./, "");
    return <Text copyable={{ text: value }}>{display}</Text>;
};

/** Renders a list of space names, each highlighted and individually copyable. */
const formatLinkedSpacesError = (spaces: string[]): React.ReactNode => (
    <div style={{ textAlign: "left" }}>
        <p style={{ marginBottom: 8 }}>Cannot delete: this group is linked to the following space(s):</p>
        <ul style={{ listStyle: "none", padding: 0, margin: "0 0 10px" }}>
            {spaces.map((s) => (
                <li key={s} style={{ marginBottom: 6 }}>
                    <Text
                        code
                        copyable={{ text: s }}
                        style={{ fontSize: 13 }}
                    >
                        {s}
                    </Text>
                </li>
            ))}
        </ul>
        <p style={{ marginBottom: 0, color: "#595959" }}>Remove the group from those spaces first.</p>
    </div>
);

/** Extracts a readable reason from a failed delete request, surfacing linked space names when present. */
const deleteErrorReason = (error: unknown): string => {
    type ContextData = { message?: string; detail?: string; linked_spaces?: string[] };
    type ErrorData = ContextData & { context?: ContextData };
    const data = (error as { response?: { data?: ErrorData } })?.response?.data;
    // Backend wraps the real payload in `context` for IAM errors (e.g. 409 LinkedToSpaces).
    const isNestedContext = !!data?.context;
    const ctx: ContextData | undefined = data?.context ?? data;
    const base = ctx?.message ?? ctx?.detail ?? (error as Error)?.message ?? "An unexpected error occurred.";
    // Nested context: the backend message already includes space names — don't duplicate them.
    if (!isNestedContext && ctx?.linked_spaces?.length) {
        return `${base} Linked space(s): ${ctx.linked_spaces.join(", ")}.`;
    }
    return base;
};

/** Extracts a short reason from a failed list request so the cause isn't swallowed. */
const loadErrorReason = (error: unknown): string => {
    type ErrorData = { message?: string; detail?: string };
    const response = (error as { response?: { status?: number; data?: ErrorData } })?.response;
    const data = response?.data;
    const reason = data?.message ?? data?.detail ?? (error as Error)?.message;
    if (!response?.status) return reason || "Unknown error";
    const suffix = reason ? `: ${reason}` : "";
    return `${response.status}${suffix}`;
};

const GroupList: React.FC<IGroupListProps> = ({ refreshKey, onCountChange }) => {
    const msalInstance = useMsal();
    // useMsal() returns a brand-new object on every MsalProvider render, and
    // MsalProvider re-renders on every MSAL event (including the ones raised by
    // acquireTokenSilent). Keeping it in a ref stops that from invalidating the
    // fetch callback and triggering a self-feeding refetch loop.
    const msalRef = useRef(msalInstance);
    msalRef.current = msalInstance;

    const onCountChangeRef = useRef(onCountChange);
    onCountChangeRef.current = onCountChange;

    // Only the most recently started fetch is allowed to write state.
    const requestIdRef = useRef(0);

    const currentUser = useMemo(() => getLoggedInUser().toLowerCase(), []);

    const [items, setItems] = useState<IGroupListItem[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [searchTerm, setSearchTerm] = useState("");
    const debouncedSearch = useDebounce(searchTerm, 250);
    const searching = searchTerm !== debouncedSearch;
    // Drives the animated deletion popup (loading → success/error).
    const [deleteProgress, setDeleteProgress] = useState<IDeleteProgress | null>(null);

    const fetchData = useCallback(async () => {
        const requestId = ++requestIdRef.current;
        setLoading(true);
        setError(null);
        try {
            const data = await listGroups(msalRef.current);
            if (requestId !== requestIdRef.current) return;
            const userGroups = data.filter((g) => {
                const email = currentUser;
                const isGovOwner = g.governance_owner?.toLowerCase() === email;
                const isGovMember = g.governance_member?.map((m) => m.toLowerCase()).includes(email) ?? false;
                const entOwners = Array.isArray(g.entitlement_owner)
                    ? g.entitlement_owner
                    : [g.entitlement_owner].filter((o): o is string => !!o);
                const isEntOwner = entOwners.map((o) => o.toLowerCase()).includes(email);
                return isGovOwner || isGovMember || isEntOwner;
            });
            const sorted = [...userGroups].sort(byNewest);
            setItems(sorted);
            onCountChangeRef.current?.(sorted.length);
        } catch (err) {
            if (requestId !== requestIdRef.current) return;
            console.error("Failed to load groups:", err);
            setError(`Failed to load groups. Please try again. (${loadErrorReason(err)})`);
        } finally {
            if (requestId === requestIdRef.current) setLoading(false);
        }
    }, [currentUser]);

    useEffect(() => {
        void fetchData();
    }, [fetchData, refreshKey]);

    /** Runs the two-step deletion (entitlement first, then governance) with live progress. */
    const runDelete = useCallback(async (item: IGroupListItem) => {
        const governance = item.governance_name || "governance group";
        const entitlement = item.entitlement_name || "entitlement group";

        const entitlementValue = item.entitlement_group_id?.trim() ?? "";
        const governanceGroupId = item.governance_group_id?.trim() ?? "";

        // Guard against missing identifiers so we never issue a delete with an
        // empty entitlement value or the wrong governance group id.
        if (!entitlementValue || !governanceGroupId) {
            setDeleteProgress({
                status: "error",
                message: "This group is missing the identifiers required to delete it. Please refresh and try again.",
            });
            return;
        }

        setDeleteProgress({
            status: "loading",
            message: `Deleting entitlement group "${entitlement}"...`,
        });

        try {
            await deleteCombinedGroup(
                msalRef.current,
                { entitlementValue, governanceGroupId },
                (phase) => {
                    if (phase === "entitlement") {
                        setDeleteProgress({
                            status: "loading",
                            message: `Entitlement group "${entitlement}" deleted. Now deleting governance group "${governance}"...`,
                        });
                    }
                },
            );

            setDeleteProgress({
                status: "success",
                message: `Governance group "${governance}" and entitlement group "${entitlement}" were deleted successfully.`,
            });
            await fetchData();
        } catch (err) {
            type ErrCtx = { message?: string; linked_spaces?: string[] };
            type ErrData = ErrCtx & { context?: ErrCtx };
            const errData = (err as { response?: { data?: ErrData } })?.response?.data;
            const linkedSpaces = errData?.context?.linked_spaces ?? errData?.linked_spaces;
            setDeleteProgress({
                status: "error",
                message: linkedSpaces?.length
                    ? formatLinkedSpacesError(linkedSpaces)
                    : `Failed to delete group: ${deleteErrorReason(err)}`,
            });
        }
    }, [fetchData]);

    /** Opens the confirmation dialog describing the two-step deletion order. */
    const confirmDelete = useCallback((item: IGroupListItem) => {
        const governance = item.governance_name || "governance group";
        const entitlement = item.entitlement_name || "entitlement group";

        Modal.confirm({
            title: "Delete this group?",
            icon: <ExclamationCircleOutlined />,
            okText: "Delete",
            okButtonProps: { danger: true },
            cancelText: "Cancel",
            content: (
                <div>
                    <p style={{ marginBottom: 12 }}>
                        The entitlement group <strong>{entitlement}</strong> will be deleted first,
                        followed by the governance group <strong>{governance}</strong>.
                    </p>
                    <Alert
                        type="warning"
                        showIcon
                        message="This action cannot be undone."
                        style={{ marginBottom: 12 }}
                    />
                    <p style={{ marginBottom: 0, color: "#8c8c8c", fontSize: 12 }}>
                        Groups linked to spaces cannot be deleted until removed from those spaces.
                    </p>
                </div>
            ),
            // void prevents onOk from receiving the Promise, so the confirm dialog
            // closes immediately instead of staying open during the async deletion.
            onOk: () => { void runDelete(item); },
        });
    }, [runDelete]);

    /** Delete-cell renderer: hidden for non-owners, disabled until completed or when linked to spaces. */
    const renderDeleteCell = (item: IGroupListItem) => {
        // Only the governance_owner can delete the group.
        const isOwner = item.governance_owner?.toLowerCase() === currentUser;
        if (!isOwner) return <Text type="secondary">—</Text>;

        const linkedSpaces = item.linked_spaces ?? [];
        if (linkedSpaces.length > 0) {
            const spaceList = linkedSpaces.join(", ");
            return (
                <Tooltip title={`Cannot delete: linked to space(s) ${spaceList}. Remove the group from those spaces first.`}>
                    <span style={{ display: "inline-block", cursor: "not-allowed" }}>
                        <Button
                            danger
                            size="small"
                            icon={<DeleteOutlined />}
                            disabled
                            style={{ pointerEvents: "none" }}
                        />
                    </span>
                </Tooltip>
            );
        }

        const isCompleted =
            item.group_creation_status?.toLowerCase() === COMPLETED_STATUS;
        if (!isCompleted) {
            return (
                <Tooltip title="Deletion is available once group creation is completed.">
                    {/* Disabled buttons don't emit mouse events, so wrap in a span
                        to keep the tooltip hoverable. */}
                    <span style={{ display: "inline-block", cursor: "not-allowed" }}>
                        <Button
                            danger
                            size="small"
                            icon={<DeleteOutlined />}
                            disabled
                            style={{ pointerEvents: "none" }}
                        />
                    </span>
                </Tooltip>
            );
        }

        return (
            <Button
                danger
                size="small"
                icon={<DeleteOutlined />}
                onClick={() => confirmDelete(item)}
            />
        );
    };

    const filteredItems = useMemo(() => {
        const q = debouncedSearch.trim().toLowerCase();
        if (!q) return items;
        return items.filter(
            (g) =>
                g.governance_name?.toLowerCase().includes(q) ||
                g.entitlement_name?.toLowerCase().includes(q),
        );
    }, [items, debouncedSearch]);

    const columns: ColumnsType<IGroupListItem> = [
        {
            title: "Governance Name",
            key: "governance_name",
            width: 220,
            render: (_, item) => copyableName(item.governance_name),
        },
        {
            title: "Entitlement Name",
            key: "entitlement_name",
            width: 220,
            render: (_, item) => copyableEntitlementName(item.entitlement_name),
        },
        {
            title: "Creation Status",
            dataIndex: "group_creation_status",
            key: "group_creation_status",
            width: 180,
            render: (value: string) => statusTag(value),
        },
        {
            title: "IDC Sync Status",
            dataIndex: "idc_sync_status",
            key: "idc_sync_status",
            width: 150,
            render: (value: string) => statusTag(value),
        },
        {
            title: "Created At",
            dataIndex: "created_at",
            key: "created_at",
            width: 170,
            render: (value: string) => formatDate(value),
        },
        {
            title: "Action",
            key: "action",
            width: 80,
            // fixed: "right" as const,
            render: (_, item) => renderDeleteCell(item),
        },
    ];

    return (
        <div
            style={{
                width: "100%",
                background: "#fff",
                padding: 24,
                borderRadius: 8,
                marginTop: 24,
            }}
        >
            <div
                style={{
                    display: "flex",
                    width: "100%",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: 16,
                }}
            >
                <Title level={5} style={{ margin: 0 }}>
                    Groups
                </Title>
                <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", justifyContent: "flex-end" }}>
                    <Input
                        placeholder="Search by governance or entitlement name"
                        aria-label="Search by governance or entitlement name"
                        prefix={<SearchOutlined style={{ color: "#bfbfbf" }} />}
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        allowClear
                        style={{ minWidth: 180, flex: 1, maxWidth: 320 }}
                    />
                    <Tooltip title="Refresh">
                        <Button
                            icon={<ReloadOutlined />}
                            onClick={fetchData}
                            loading={loading}
                            aria-label="Refresh"
                        />
                    </Tooltip>
                </div>
            </div>

            {error && (
                <Alert
                    type="error"
                    showIcon
                    closable
                    message={error}
                    onClose={() => setError(null)}
                    style={{ marginBottom: 16 }}
                />
            )}

            <Table
                rowKey="group_id"
                size="middle"
                bordered
                loading={loading || searching}
                columns={columns}
                dataSource={filteredItems}
                scroll={{ x: 1020 }}
                pagination={{ pageSize: 10, hideOnSinglePage: true }}
            />

            {deleteProgress && (
                <ResultPopup
                    open={!!deleteProgress}
                    status={deleteProgress.status}
                    message={deleteProgress.message}
                    title={deleteProgress.status === "loading" ? "Deleting group" : undefined}
                    onClose={() => setDeleteProgress(null)}
                />
            )}
        </div>
    );
};

export default GroupList;
